import { makeStyles } from '@material-ui/core/styles'

const useStyles = makeStyles((theme) => ({
  scrollOuter: ({ dense, alignment }) => ({
    /**
     * Outer scroll container for the tab strip. Sized to its content
     * (`width: max-content`) and capped at the parent (`max-width:
     * 100%`), then centered with `marginInline: auto`. This makes the
     * strip a content-sized block centered in its parent — there is
     * no flex-layout ambiguity around how free space gets distributed,
     * which made the previous auto-margin-on-flex-item approach center
     * inconsistently at some viewports. When the tab row exceeds the
     * parent width the outer collapses to `100%` and scrolls from the
     * start, so the leftmost tab stays visible and the natural
     * "scroll from the start" UX takes over via `overflowX: 'auto'`.
     */
    display: 'block',
    width: 'max-content',
    maxWidth: '100%',
    marginInlineStart: alignment === 'left' ? 0 : 'auto',
    marginInlineEnd: alignment === 'left' ? 0 : 'auto',
    overflowX: 'auto',
    WebkitOverflowScrolling: 'touch',
    scrollbarWidth: 'none',
    '&::-webkit-scrollbar': { display: 'none' },
    marginTop: dense ? theme.spacing(2) : theme.spacing(3),
    marginBottom: dense ? theme.spacing(2) : theme.spacing(5),
    paddingTop: theme.spacing(0.5),
    paddingBottom: theme.spacing(0.5),
    paddingLeft: theme.spacing(0.5),
    paddingRight: theme.spacing(0.5),
  }),
  root: () => ({
    display: 'inline-flex',
    /**
     * Inter-tab gap. Tightened from `spacing(1.5)` (12px) to
     * `spacing(1)` (8px) so the row's natural width stays compact;
     * the centering itself is owned by `scrollOuter` (content-sized,
     * `marginInline: auto`).
     */
    gap: theme.spacing(1),
    textAlign: 'left',
  }),
  tab: {
    ...theme.typography.formTags,
    '&.MuiButtonBase-root': {
      minWidth: 0,
    },
    flexShrink: 0,
    /**
     * Center the label both axes inside the 45px pill. Anchors (the
     * `next/link` tab variant) don't get MUI Button's internal flex
     * centering, so without `display: inline-flex` here the text
     * settles to the natural baseline near the top of the pill rather
     * than appearing optically centered. Matching the icon-only tab's
     * centering pattern keeps the two visually consistent.
     */
    display: 'inline-flex',
    alignItems: 'center',
    justifyContent: 'center',
    paddingLeft: theme.spacing(1.5),
    paddingRight: theme.spacing(1.5),
    height: '45px',
    textAlign: 'center',
    borderRadius: '8px',
    border: 'none',
    whiteSpace: 'nowrap',
    fontSize: '14px',
    transition: '250ms',
    backgroundColor: '#f4f6f8',
    color: theme.palette.secondary.main,
    textDecoration: 'none',
    cursor: 'pointer',
    [theme.breakpoints.down('xs')]: {
      ...theme.typography.formLabel,
      fontSize: '12px',
      paddingLeft: theme.spacing(1.5),
      paddingRight: theme.spacing(1.5),
    },
    '&:hover, &:active': {
      textDecoration: 'none',
      backgroundColor: '#e9edf2',
      color: theme.palette.secondary.main,
    },
    '&:focus-visible': {
      outline: 'none',
      boxShadow: `inset 0 0 0 2px ${theme.palette.blue.main}`,
    },
  },
  active: {
    backgroundColor: theme.palette.blue.main,
    color: theme.palette.offwhite.main,
    '&:focus-visible': {
      outline: 'none',
      boxShadow: `inset 0 0 0 2px ${theme.palette.common.white}`,
    },
    '&:hover, &:active': {
      backgroundColor: theme.palette.blue.main,
      color: theme.palette.offwhite.main,
    },
  },
  tabIconOnly: {
    /**
     * Render icon-only tabs as a square pill that matches the strip
     * height so the icon doesn't read as a tucked-in scrap next to
     * the wider text tabs. 45×45 mirrors the base `tab` height,
     * paddings collapse to 0 because we no longer need horizontal
     * room for a label, and `minWidth` overrides the MUI Button
     * baseline so the explicit `width` actually applies.
     */
    fontSize: 22,
    lineHeight: 1,
    width: 45,
    minWidth: 45,
    paddingLeft: 0,
    paddingRight: 0,
    display: 'inline-flex',
    alignItems: 'center',
    justifyContent: 'center',
    '&.MuiButtonBase-root': {
      minWidth: 45,
    },
    [theme.breakpoints.down('xs')]: {
      fontSize: 22,
    },
  },
  tabLeadingIcon: {
    display: 'block',
  },
  tabDivider: {
    alignSelf: 'center',
    width: 1,
    height: 24,
    flexShrink: 0,
    backgroundColor: theme.palette.grey.light,
    opacity: 0.85,
    marginLeft: theme.spacing(0.25),
    marginRight: theme.spacing(0.25),
  },
}))

export default useStyles
